import { useEffect, useRef, useState } from 'react';
import type { AnimationItem } from 'lottie-web';

interface LottieMarker {
  cm: string;
  tm: number;
  dr: number;
}

interface LottieAnimationProps {
  // The animation's JSON, loaded only once it is shown.
  load: () => Promise<object>;
  loop?: boolean;
  // Plays up to this marker once, then loops only the marker's frames.
  loopMarker?: string;
  className?: string;
  // Played to its end (never when looping), or could not be played.
  onComplete?: () => void;
}

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * A Lottie animation, with the player loaded on demand. Not played at all for
 * users who ask for reduced motion: whatever stands in for it stays.
 */
export function LottieAnimation({
  load,
  loop = false,
  loopMarker,
  className,
  onComplete,
}: LottieAnimationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlayable] = useState(() => !prefersReducedMotion());

  useEffect(() => {
    if (!isPlayable) {
      onComplete?.();
      return;
    }

    let animation: AnimationItem | undefined;
    let isCancelled = false;

    Promise.all([import('lottie-web/build/player/lottie_light'), load()])
      .then(([{ default: lottie }, animationData]) => {
        if (isCancelled || !containerRef.current) return;

        animation = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop: loop || loopMarker !== undefined,
          autoplay: true,
          animationData,
          rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
        });
        const marker = (
          animationData as { markers?: LottieMarker[] }
        ).markers?.find(({ cm }) => cm === loopMarker);
        if (marker) {
          animation.playSegments(
            [
              [0, marker.tm],
              [marker.tm, marker.tm + marker.dr],
            ],
            true,
          );
        }
        animation.addEventListener('complete', () => onComplete?.());
      })
      .catch(() => onComplete?.());

    return () => {
      isCancelled = true;
      animation?.destroy();
    };
  }, []);

  if (!isPlayable) return null;

  return <div ref={containerRef} className={className} aria-hidden />;
}
